import
{
    AccountCircle,
    ArrowBack,
    ArrowForward,
    Business,
    CheckCircle,
    Groups,
    Person,
    VpnKey,
} from '@mui/icons-material';
import
{
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { clearRegistrationToken, getRegistrationToken } from '@src/App/Utils/registrationToken';
import { stageContext } from '@src/globals';
import type { AppState } from '@store/store';
import { Shield } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// TODO: Create proper TypeScript interfaces for form data
interface CompanyRegistrationFormData
{
    // Step 1: Invitation
    inviteCode: string;

    // Step 2: Personal Info
    firstName: string;
    lastName: string;
    jobTitle: string;

    // Step 3: Company Data
    companyName: string;
    companyId: string;
    teamSize: string;
    industry: string;

    // Step 4: Goals
    goals: string[];
    experience: string;
}

/**
 * Company Registration Stepper Page Component
 * Based on landing page onboarding design
 */
function CompanyRegistrationPage()
{
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).registerCompany;

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<CompanyRegistrationFormData>({
        inviteCode: '',
        firstName: '',
        lastName: '',
        jobTitle: '',
        companyName: '',
        companyId: '',
        teamSize: '',
        industry: '',
        goals: [],
        experience: '',
    });

    const [companyIdChanged, setCompanyIdChanged] = useState(false);
    const [validatingInvite, setValidatingInvite] = useState(false);
    const [inviteValid, setInviteValid] = useState<boolean | null>(null);
    const [validatingCompanyId, setValidatingCompanyId] = useState(false);
    const [companyIdValid, setCompanyIdValid] = useState<boolean | null>(null);
    const [creating, setCreating] = useState(false);

    // Suppress ResizeObserver error on mobile
    useEffect(() =>
    {
        const originalError = console.error;
        const originalOnError = window.onerror;

        console.error = (...args) =>
        {
            if (args[0]?.includes?.('ResizeObserver loop completed'))
            {
                return;
            }
            originalError(...args);
        };

        window.onerror = (message, source, lineno, colno, error) =>
        {
            if (message?.toString().includes('ResizeObserver loop completed'))
            {
                return true; // Prevent default error handling
            }
            return originalOnError ? originalOnError(message, source, lineno, colno, error) : false;
        };

        return () =>
        {
            console.error = originalError;
            window.onerror = originalOnError;
        };
    }, []);

    const totalSteps = 5;
    const progress = (currentStep / totalSteps) * 100;

    const handleInputChange = (field: keyof CompanyRegistrationFormData, value: string | string[]) =>
    {
        setFormData((prev) => ({ ...prev, [field]: value }));

        // Auto-format companyId from companyName
        if (field === 'companyName' && !companyIdChanged)
        {
            setFormData((prev) => ({ ...prev, companyId: formatCompanyId(value as string) }));
        }

        // Reset validation when companyId changes
        if (field === 'companyId')
        {
            setCompanyIdChanged(true);
            setCompanyIdValid(null);
        }
    };

    const handleGoalToggle = (goal: string) =>
    {
        setFormData((prev) => ({
            ...prev,
            goals: prev.goals.includes(goal) ? prev.goals.filter((g) => g !== goal) : [...prev.goals, goal],
        }));
    };

    const nextStep = () =>
    {
        if (currentStep < totalSteps)
        {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () =>
    {
        if (currentStep > 1)
        {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleComplete = () =>
    {
        setCreating(true);
        const userToken = getRegistrationToken();

        createCompany(userToken, formData.companyId, formData.companyName, formData.inviteCode)
            .then(() =>
            {
                setCreating(false);
                clearRegistrationToken();
                navigate(`/${ELinks.DASHBOARD}`);
            })
            .catch((error) =>
            {
                console.error('Company creation failed:', error);
                setCreating(false);
            });
    };

    const getStepIcon = () =>
    {
        switch (currentStep)
        {
            case 1:
                return <VpnKey sx={{ fontSize: 32, color: 'primary.main' }} />;
            case 2:
                return <Person sx={{ fontSize: 32, color: 'primary.main' }} />;
            case 3:
                return <Business sx={{ fontSize: 32, color: 'primary.main' }} />;
            case 4:
                return <Groups sx={{ fontSize: 32, color: 'primary.main' }} />;
            case 5:
                return <CheckCircle sx={{ fontSize: 32, color: 'primary.main' }} />;
            default:
                return <VpnKey sx={{ fontSize: 32, color: 'primary.main' }} />;
        }
    };

    const getStepTitle = () =>
    {
        switch (currentStep)
        {
            case 1:
                return langStrings.step1;
            case 2:
                return 'Persönliche Informationen';
            case 3:
                return langStrings.step3;
            case 4:
                return 'Ihre Ziele';
            case 5:
                return langStrings.step4;
            default:
                return langStrings.step1;
        }
    };

    const getStepDescription = () =>
    {
        switch (currentStep)
        {
            case 1:
                return 'Geben Sie Ihren Einladungscode ein';
            case 2:
                return 'Erzählen Sie uns etwas über sich';
            case 3:
                return 'Grundlegende Informationen zu Ihrem Unternehmen';
            case 4:
                return 'Was möchten Sie mit T4LO erreichen?';
            case 5:
                return 'Bestätigen Sie Ihre Angaben';
            default:
                return '';
        }
    };

    const isStepValid = () =>
    {
        switch (currentStep)
        {
            case 1:
                return inviteValid === true;
            case 2:
                return formData.firstName && formData.lastName;
            case 3:
                return formData.companyName && formData.companyId && companyIdValid === true;
            case 4:
                return formData.goals.length > 0;
            case 5:
                return true;
            default:
                return false;
        }
    };

    // Validate invite code with debounce
    useEffect(() =>
    {
        if (!formData.inviteCode)
        {
            setInviteValid(null);
            return;
        }

        setValidatingInvite(true);
        const timer = setTimeout(() =>
        {
            postInviteCode(formData.inviteCode)
                .then((valid) =>
                {
                    setInviteValid(valid);
                    setValidatingInvite(false);
                })
                .catch(() =>
                {
                    setInviteValid(false);
                    setValidatingInvite(false);
                });
        }, 1500);

        return () => clearTimeout(timer);
    }, [formData.inviteCode]);

    // Validate companyId with debounce
    useEffect(() =>
    {
        if (!formData.companyId)
        {
            setCompanyIdValid(null);
            return;
        }

        setValidatingCompanyId(true);
        const timer = setTimeout(() =>
        {
            const userToken = getRegistrationToken();
            validateCompanyId(userToken, formData.companyId)
                .then((valid) =>
                {
                    setCompanyIdValid(valid);
                    setValidatingCompanyId(false);
                })
                .catch(() =>
                {
                    setCompanyIdValid(false);
                    setValidatingCompanyId(false);
                });
        }, 1500);

        return () => clearTimeout(timer);
    }, [formData.companyId]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'transparent',
            }}
        >
            <Box
                sx={{
                    maxWidth: { xs: '100%', sm: 600, md: 800 },
                    mx: 'auto',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 2, sm: 4 },
                }}
            >
                {/* Header with Progress */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: { xs: 3, sm: 4 },
                        gap: { xs: 2, sm: 0 },
                    }}
                >
                    <Typography variant="h6" color="text.secondary">
                        {langStrings.register}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            aria-live="polite"
                            aria-label={`Aktueller Fortschritt: Schritt ${currentStep} von ${totalSteps}`}
                        >
                            Schritt {currentStep} von {totalSteps}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ width: { xs: 100, sm: 120 }, height: 6, borderRadius: 3 }}
                            aria-label={`Fortschritt: ${Math.round(progress)}% abgeschlossen`}
                        />
                    </Box>
                </Box>

                <Card
                    sx={{
                        boxShadow: theme.shadows[8],
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.paper',
                    }}
                    role="main"
                    aria-labelledby="step-title"
                    aria-describedby="step-description"
                >
                    {/* Step Header */}
                    <Box sx={{ textAlign: 'center', pt: { xs: 4, sm: 6 }, pb: 3 }}>
                        <Box
                            sx={{
                                width: { xs: 60, sm: 80 },
                                height: { xs: 60, sm: 80 },
                                bgcolor: (theme) =>
                                    theme.palette.mode === 'dark' ? 'rgba(0, 213, 169, 0.15)' : 'secondary.light',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3,
                            }}
                            role="img"
                            aria-label={`Schritt ${currentStep} Symbol`}
                        >
                            {getStepIcon()}
                        </Box>
                        <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom id="step-title">
                            {getStepTitle()}
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ maxWidth: 500, mx: 'auto', px: { xs: 2, sm: 0 } }}
                            id="step-description"
                        >
                            {getStepDescription()}
                        </Typography>
                    </Box>

                    <CardContent sx={{ px: { xs: 2, sm: 3, md: 6 }, pb: 4 }}>
                        {/* Step 1: Invitation Code */}
                        {currentStep === 1 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label={langStrings.inviteCode}
                                    required
                                    fullWidth
                                    size={isMobile ? 'medium' : 'small'}
                                    value={formData.inviteCode}
                                    onChange={(e) => handleInputChange('inviteCode', e.target.value.toUpperCase())}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <VpnKey aria-hidden="true" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    color={inviteValid === true ? 'success' : 'primary'}
                                    error={inviteValid === false}
                                    helperText={
                                        inviteValid === false
                                            ? langStrings.inviteCodeInvalid
                                            : inviteValid === true
                                                ? langStrings.inviteCodeValid
                                                : validatingInvite
                                                    ? langStrings.validating
                                                    : 'Bitte gib den Code ein, den du erhalten hast. Ohne diesen Code ist eine Registrierung nicht möglich.'
                                    }
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                                    aria-describedby="invite-code-helper"
                                    inputProps={{
                                        'aria-label': 'Einladungscode eingeben',
                                        autoComplete: 'off',
                                    }}
                                />
                            </Box>
                        )}

                        {/* Step 2: Personal Information */}
                        {currentStep === 2 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: { xs: 2, sm: 3 },
                                        mb: 3,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                        <AccountCircle sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                                        <TextField
                                            label="Vorname *"
                                            variant="standard"
                                            fullWidth
                                            value={formData.firstName}
                                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                                            placeholder="Max"
                                            autoFocus
                                            inputProps={{
                                                'aria-label': 'Vorname eingeben',
                                                autoComplete: 'given-name',
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                        <AccountCircle sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                                        <TextField
                                            label="Nachname *"
                                            variant="standard"
                                            fullWidth
                                            value={formData.lastName}
                                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                                            placeholder="Mustermann"
                                            inputProps={{
                                                'aria-label': 'Nachname eingeben',
                                                autoComplete: 'family-name',
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', minHeight: 48 }}>
                                    <Shield
                                        size={24}
                                        style={{
                                            color: 'rgba(0, 0, 0, 0.54)',
                                            marginRight: 8,
                                            marginBottom: 4,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <TextField
                                        label="Position"
                                        variant="standard"
                                        fullWidth
                                        value={formData.jobTitle}
                                        onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                                        placeholder="z.B. HR Manager, Team Lead, CEO"
                                        sx={{ '& .MuiInputBase-root': { minHeight: 24 } }}
                                        inputProps={{
                                            'aria-label': 'Position oder Jobtitel eingeben',
                                            autoComplete: 'organization-title',
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Step 3: Company Data */}
                        {currentStep === 3 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label={langStrings.companyDataCompanyName}
                                    required
                                    fullWidth
                                    size={isMobile ? 'medium' : 'small'}
                                    value={formData.companyName}
                                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                                    helperText={langStrings.companyDataCompanyNameHelper}
                                    autoFocus
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                            minHeight: isMobile ? 56 : 40,
                                        },
                                        '& .MuiInputLabel-root': {
                                            transform: isMobile
                                                ? 'translate(14px, 16px) scale(1)'
                                                : 'translate(14px, 9px) scale(1)',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                            },
                                        },
                                    }}
                                    inputProps={{
                                        'aria-label': 'Firmenname eingeben',
                                        autoComplete: 'organization',
                                    }}
                                />
                                <TextField
                                    label={langStrings.companyDataCompanyId}
                                    required
                                    fullWidth
                                    size={isMobile ? 'medium' : 'small'}
                                    value={formData.companyId}
                                    onChange={(e) => handleInputChange('companyId', e.target.value)}
                                    color={companyIdValid === true ? 'success' : 'primary'}
                                    error={companyIdValid === false}
                                    helperText={
                                        companyIdValid === false
                                            ? langStrings.companyDataCompanyIdInvalid
                                            : companyIdValid === true
                                                ? langStrings.companyDataCompanyIdValid
                                                : validatingCompanyId
                                                    ? langStrings.validating
                                                    : langStrings.companyDataCompanyIdHelper
                                    }
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                            minHeight: isMobile ? 56 : 40,
                                        },
                                        '& .MuiInputLabel-root': {
                                            transform: isMobile
                                                ? 'translate(14px, 16px) scale(1)'
                                                : 'translate(14px, 9px) scale(1)',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                            },
                                        },
                                    }}
                                    inputProps={{
                                        'aria-label': 'Firmenkürzel eingeben',
                                        autoComplete: 'off',
                                    }}
                                />
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                            minHeight: isMobile ? 56 : 40,
                                        },
                                        '& .MuiInputLabel-root': {
                                            transform: isMobile
                                                ? 'translate(14px, 16px) scale(1)'
                                                : 'translate(14px, 9px) scale(1)',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                            },
                                        },
                                        mt: 1,
                                    }}
                                >
                                    <InputLabel id="team-size-label">Teamgröße</InputLabel>
                                    <Select
                                        value={formData.teamSize}
                                        onChange={(e) => handleInputChange('teamSize', e.target.value)}
                                        label="Teamgröße"
                                        labelId="team-size-label"
                                        aria-label="Teamgröße auswählen"
                                    >
                                        <MenuItem value="1-10">1-10 Personen</MenuItem>
                                        <MenuItem value="11-50">11-50 Personen</MenuItem>
                                        <MenuItem value="51-200">51-200 Personen</MenuItem>
                                        <MenuItem value="201-1000">201-1000 Personen</MenuItem>
                                        <MenuItem value="1000+">1000+ Personen</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                            minHeight: isMobile ? 56 : 40,
                                        },
                                        '& .MuiInputLabel-root': {
                                            transform: isMobile
                                                ? 'translate(14px, 16px) scale(1)'
                                                : 'translate(14px, 9px) scale(1)',
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                            },
                                        },
                                        mt: 1,
                                    }}
                                >
                                    <InputLabel id="industry-label">Branche</InputLabel>
                                    <Select
                                        value={formData.industry}
                                        onChange={(e) => handleInputChange('industry', e.target.value)}
                                        label="Branche"
                                        labelId="industry-label"
                                        aria-label="Branche auswählen"
                                    >
                                        <MenuItem value="technology">Technologie</MenuItem>
                                        <MenuItem value="finance">Finanzwesen</MenuItem>
                                        <MenuItem value="healthcare">Gesundheitswesen</MenuItem>
                                        <MenuItem value="education">Bildung</MenuItem>
                                        <MenuItem value="manufacturing">Produktion</MenuItem>
                                        <MenuItem value="consulting">Beratung</MenuItem>
                                        <MenuItem value="retail">Einzelhandel</MenuItem>
                                        <MenuItem value="other">Andere</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        {/* Step 4: Goals */}
                        {currentStep === 4 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <Typography
                                    variant="body1"
                                    fontWeight={500}
                                    component="fieldset"
                                    role="group"
                                    aria-labelledby="goals-legend"
                                >
                                    <legend id="goals-legend">
                                        Was sind Ihre Hauptziele mit T4LO? (Mehrfachauswahl möglich)
                                    </legend>
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {[
                                        'Skill-Gaps identifizieren',
                                        'Mitarbeiterentwicklung verbessern',
                                        'Onboarding optimieren',
                                        'Performance Management',
                                        'Karriereplanung unterstützen',
                                        'Team-Kompetenzen analysieren',
                                    ].map((goal) => (
                                        <Card
                                            key={goal}
                                            sx={{
                                                p: { xs: 1, sm: 1.5 },
                                                cursor: 'pointer',
                                                border: formData.goals.includes(goal)
                                                    ? `2px solid ${theme.palette.secondary.main}`
                                                    : `1px solid ${theme.palette.divider}`,
                                                bgcolor: formData.goals.includes(goal)
                                                    ? theme.palette.mode === 'dark'
                                                        ? 'rgba(0, 213, 169, 0.15)'
                                                        : 'secondary.light'
                                                    : 'background.paper',
                                                '&:hover': {
                                                    borderColor: 'secondary.main',
                                                },
                                                '&:focus-within': {
                                                    borderColor: 'primary.main',
                                                    outline: `2px solid ${theme.palette.primary.main}`,
                                                },
                                            }}
                                            onClick={() => handleGoalToggle(goal)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) =>
                                            {
                                                if (e.key === 'Enter' || e.key === ' ')
                                                {
                                                    e.preventDefault();
                                                    handleGoalToggle(goal);
                                                }
                                            }}
                                            aria-pressed={formData.goals.includes(goal)}
                                            aria-label={`${goal} ${formData.goals.includes(goal) ? 'ausgewählt' : 'nicht ausgewählt'}`}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.goals.includes(goal)}
                                                        onChange={() => handleGoalToggle(goal)}
                                                        aria-label={goal}
                                                        tabIndex={-1}
                                                    />
                                                }
                                                label={goal}
                                                sx={{ m: 0, width: '100%' }}
                                            />
                                        </Card>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Step 5: Confirmation */}
                        {currentStep === 5 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {/* Confirmation Section */}
                                <Card
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        background: (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? 'linear-gradient(135deg, rgba(64, 61, 219, 0.1) 0%, rgba(236, 230, 240, 0.05) 100%)'
                                                : 'linear-gradient(135deg, rgba(64, 61, 219, 0.05) 0%, rgba(236, 230, 240, 0.1) 100%)',
                                    }}
                                >
                                    <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                                        Angaben bestätigen
                                    </Typography>

                                    <Box
                                        sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
                                                gap: 2,
                                                width: '100%',
                                                maxWidth: 400,
                                            }}
                                        >
                                            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Firmenname:
                                            </Typography>
                                            <Typography variant="body1" fontWeight={600}>
                                                {formData.companyName}
                                            </Typography>

                                            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Firmenkürzel:
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                fontWeight={600}
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                {formData.companyId}
                                            </Typography>

                                            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Teamgröße:
                                            </Typography>
                                            <Typography variant="body1" fontWeight={600}>
                                                {formData.teamSize || 'Nicht angegeben'}
                                            </Typography>

                                            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Branche:
                                            </Typography>
                                            <Typography variant="body1" fontWeight={600}>
                                                {formData.industry || 'Nicht angegeben'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Card>

                                {/* Next Steps Section */}
                                <Card sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                                        Nächste Schritte
                                    </Typography>

                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ textAlign: 'center', mb: 4 }}
                                    >
                                        Wählen Sie, wie Sie mit T4LO starten möchten:
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            fullWidth
                                            sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                borderWidth: 2,
                                                '&:hover': {
                                                    borderWidth: 2,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: theme.shadows[8],
                                                },
                                            }}
                                            onClick={() => navigate(`/${ELinks.DASHBOARD}`)}
                                        >
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="h6" gutterBottom>
                                                    Dashboard erkunden
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Erkunden Sie die Möglichkeiten von T4LO
                                                </Typography>
                                            </Box>
                                        </Button>

                                        <Button
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                background: 'linear-gradient(45deg, #403ddb 30%, #00D5A9 90%)',
                                                '&:hover': {
                                                    background: 'linear-gradient(45deg, #2d2a9e 30%, #00c299 90%)',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: theme.shadows[12],
                                                },
                                            }}
                                            onClick={() => navigate(`/${ELinks.PROFILE_EDIT}`)}
                                        >
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                                                    Onboarding starten
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                                    Profil ausfüllen und Assessment durchführen
                                                </Typography>
                                            </Box>
                                        </Button>
                                    </Box>
                                </Card>
                            </Box>
                        )}

                        {/* Navigation Buttons */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                mt: { xs: 4, sm: 6 },
                                gap: { xs: 2, sm: 0 },
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={prevStep}
                                disabled={currentStep === 1}
                                startIcon={<ArrowBack aria-hidden="true" />}
                                sx={{
                                    borderRadius: '8px',
                                    order: { xs: 2, sm: 1 },
                                    minHeight: { xs: 48, sm: 'auto' },
                                }}
                                aria-label="Zum vorherigen Schritt zurückkehren"
                            >
                                Zurück
                            </Button>

                            {currentStep < totalSteps ? (
                                <Button
                                    variant="contained"
                                    onClick={nextStep}
                                    disabled={!isStepValid() || validatingInvite || validatingCompanyId}
                                    endIcon={<ArrowForward aria-hidden="true" />}
                                    sx={{
                                        borderRadius: '8px',
                                        order: { xs: 1, sm: 2 },
                                        minHeight: { xs: 48, sm: 'auto' },
                                    }}
                                    aria-label="Zum nächsten Schritt weitergehen"
                                >
                                    Weiter
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleComplete}
                                    disabled={creating}
                                    endIcon={<ArrowForward aria-hidden="true" />}
                                    sx={{
                                        borderRadius: '8px',
                                        background: 'linear-gradient(45deg, #403ddb 30%, #00D5A9 90%)',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #2d2a9e 30%, #00c299 90%)',
                                        },
                                        order: { xs: 1, sm: 2 },
                                        minHeight: { xs: 48, sm: 'auto' },
                                    }}
                                    aria-label="Registrierung abschließen und zum Dashboard gehen"
                                >
                                    {creating ? 'Erstelle Firma...' : 'Zum Dashboard'}
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

// ============================================================================
// Backend API Functions
// ============================================================================

/**
 * Format company ID to lowercase alphanumeric
 */
function formatCompanyId(input: string): string
{
    let formatted = input.toLowerCase();
    formatted = formatted.replace(/[^a-z0-9]/g, '');
    formatted = formatted.substring(0, 20);
    return formatted;
}

/**
 * Validate invite code via API
 */
const postInviteCode = async (inviteCode: string): Promise<boolean> =>
{
    const url = `${stageContext.apiGatewayRestDomain}/validateInviteCode`;
    const body = JSON.stringify({ inviteCode });

    try
    {
        const response = await fetch(url, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
            },
            body: body,
        });

        if (!response.ok)
        {
            throw new Error(`HTTP-Fehler: Status ${response.status}`);
        }

        const data = await response.json();
        return data.valid;
    } catch (error)
    {
        console.error('Fehler beim Validieren des Invite Codes:', error);
        return false;
    }
};

/**
 * Validate company ID via API
 */
const validateCompanyId = async (token: string, companyId: string): Promise<boolean> =>
{
    const url = `${stageContext.apiGatewayRestDomain}/validateCompanyId`;
    const body = JSON.stringify({ companyId });

    try
    {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
            body: body,
        });

        if (!response.ok)
        {
            throw new Error(`HTTP-Fehler: Status ${response.status}`);
        }

        const data = await response.json();
        return data.valid;
    } catch (error)
    {
        console.error('Fehler beim Validieren der Company ID:', error);
        throw new Error('Error Fetch');
    }
};

/**
 * Create company via API
 */
const createCompany = async (token: string, companyId: string, title: string, inviteCode: string): Promise<boolean> =>
{
    const url = `${stageContext.apiGatewayRestDomain}/createCompany`;
    const body = JSON.stringify({ companyId, title, inviteCode });

    try
    {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
            body: body,
        });

        if (!response.ok)
        {
            throw new Error(`HTTP-Fehler: Status ${response.status}`);
        }

        const data = await response.json();
        return data.created;
    } catch (error)
    {
        console.error('Fehler beim Erstellen der Company:', error);
        throw new Error('Error Fetch');
    }
};

export default CompanyRegistrationPage;
